<template>
  <section class="c mapbox">
    <mapbox-component
      :accessToken="accessToken"
      :map-options="{
        style: mapStyle,
        center: center,
        zoom: zoom
      }"
      :geolocate-control="geolocateControl"
      :scale-control="scaleControl"
      :fullscreen-control="fullscreenControl"
      @map-init="mapInitialized"
      @map-load="mapLoaded"
      @map-zoom="emitEvent('zoom', map)"
      @map-drag="emitEvent('drag', map)"
      @map-zoomstart="emitEvent('zoom-start')"
      @map-zoomend="emitEvent('zoom-end')"
      @map-dragstart="emitEvent('drag-start')"
      @map-dragend="emitEvent('drag-end')"
      @map-rotate="emitEvent('rotate')"
      @map-rotatestart="emitEvent('rotate-start')"
      @map-rotateend="emitEvent('rotate-end')"
    ></mapbox-component>
    <transition name="fade">
      <div class="loading" v-show="isLoading">
        <v-progress-linear :indeterminate="true"></v-progress-linear>
      </div>
    </transition>
  </section>
</template>

<script>
import MapboxComponent from 'mapbox-gl-vue';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

export default {
  name: 'mapbox',

  components: {
    MapboxComponent,
  },

  props: {
    center: {
      type: Array,
      required: false,
      default() {
        return [10.013850, 53.548240];
      },
    },
    layer: {
      type: Array,
    },
    zoom: {
      type: Number,
      default: 16,
    },
    marker: {
      type: Array,
    },
    drawMode: {
      type: Boolean,
      default: false,
    },
    accessToken: {
      type: String,
      required: true,
    },
    mapStyle: {
      type: String,
      required: true,
    },
    loadHandling: {
      type: Boolean,
      default: false,
    },
    geolocateControl: {
      type: Object,
      default() {
        return {
          show: true,
          position: 'top-left',
        };
      },
    },
    scaleControl: {
      type: Object,
      default() {
        return {
          show: true,
          position: 'top-left',
        };
      },
    },
    fullscreenControl: {
      type: Object,
      default() {
        return {
          show: false,
          position: 'top-left',
        };
      },
    },
    setDragMarker: {
      type: Boolean,
      default: false,
    },
    setDragMarkerCoords: {
      type: Array,
      default() {
        return [0, 0];
      },
    },
  },

  data() {
    return {
      markerSet: [],
      draw: null,
      isLoading: true,
      mapIsLoaded: false,
      layerSet: [],
    };
  },

  watch: {
    center() {
      this.setPosition(this.map);
    },
    zoom() {
      this.setPosition(this.map);
    },
    drawMode() {
      if (this.drawMode) {
        this.initDrawMode(this.map);
      } else {
        this.removeDrawMode(this.map);
      }
    },
    marker() {
      this.setMarker(this.map);
      this.getMarkersInBounds(this.map);
    },
    setDragMarker(newVal) {
      this.setDraggableMarker(newVal);
    },
    layer() {
      // avoid 'map style was not loaded yet' error
      setTimeout(() => {
        this.setLayer(this.map);
      }, 250);
    },
  },

  methods: {
    mapLoaded(map) {
      this.emitEvent('load', map);
      this.mapIsLoaded = true;
      this.$nextTick(() => {
        this.setLayer(map);
        this.setMarker(map);
        if (this.drawMode) {
          this.initDrawMode(map);
        }
        this.isLoading = false;
      });
    },
    mapInitialized(map) {
      const Observer = Object.getPrototypeOf(this.$data.__ob__).constructor;
      map.__ob__ = new Observer({});
      this.map = map;
      this.setPosition(map);
      this.emitEvent('init', map);
    },
    setPosition(map) {
      map.setCenter(this.center);
      map.setZoom(this.zoom);
    },
    setLayer(map) {
      this.layerSet = [];
      if (this.layer) {
        this.layer.map((layer) => {
          map.addLayer(layer);
          if (layer.eventListener) {
            map.on('click', layer.id, () => {
              this.emitEvent('layer-click', layer);
            });
          }
        });
      }
    },
    setMarker(map) {
      this.markerSet.map((marker) => {
        marker.remove();
      });
      this.markerSet = [];
      if (this.marker) {
        this.marker.map((marker, index) => {
          const el = this.$el.querySelector(`#marker-${marker.id}`);
          if (!el) {
            const el = document.createElement('div');
            if (marker.id) el.id = `marker-${marker.id}`;
            if (marker.className) el.className = marker.className;
            if (marker.backgroundColor) el.style.backgroundColor = marker.backgroundColor;
            if (marker.textColor) el.style.color = marker.textColor;
            if (marker.innerHTML) el.innerHTML = marker.innerHTML;
            if (marker.eventListener) {
              el.addEventListener('mouseover', () => {
                this.emitEvent('marker-hover', { id: marker.id, color: marker.backgroundColor });
                el.classList.add('on-map-focus');
              });
              el.addEventListener('mouseleave', () => {
                this.emitEvent('marker-hover', { id: marker.id, color: null });
                el.classList.remove('on-map-focus');
              });
              el.addEventListener('click', () => {
                this.emitEvent('marker-click', marker.id);
              });
            }

            const mapMarker = new mapboxgl.Marker(el)
              .setLngLat(marker.geoJson.geometry.coordinates)
              .addTo(map);
            mapMarker._id = marker.id;
            this.markerSet.push(mapMarker);
          }
        });
      }
    },
    inBounds(point, bounds) {
      const lng = (point.lng - bounds._ne.lng) * (point.lng - bounds._sw.lng) < 0;
      const lat = (point.lat - bounds._ne.lat) * (point.lat - bounds._sw.lat) < 0;
      return lng && lat;
    },
    getMarkersInBounds() {
      const markerIdsInBounds = [];
      this.markerSet.map((marker, index) => {
        const inBounds = this.inBounds(marker._lngLat, this.map.getBounds());
        if (inBounds) {
          markerIdsInBounds.push(marker._id);
        }
      });
      return markerIdsInBounds;
    },
    emitEvent(eventName, args) {
      this.$emit(eventName, args);
    },
    initDrawMode(map) {
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true,
        },
      });
      map.addControl(this.draw, 'top-right');
      map.on('draw.create', this.updateArea);
      map.on('draw.delete', this.updateArea);
      map.on('draw.update', this.updateArea);
    },
    removeDrawMode(map) {
      map.removeControl(this.draw);
      this.draw = null;
    },
    updateArea(e) {
      this.$emit('draw', e);
    },
    setDraggableMarker(bool) {
      console.log('drag marker setup');
      if (bool) {
        const canvas = this.map.getCanvasContainer();

        const geojson = {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: this.setDragMarkerCoords,
            },
          }],
        };

        const onMove = (e) => {
          const coords = e.lngLat;
          canvas.style.cursor = 'grabbing';
          geojson.features[0].geometry.coordinates = [coords.lng, coords.lat];
          this.map.getSource('point').setData(geojson);
        };

        const onUp = (e) => {
          const coords = e.lngLat;
          this.$emit('marker-set', coords);
          canvas.style.cursor = '';

          this.map.off('mousemove', onMove);
          this.map.off('touchmove', onMove);
        };

        this.map.addSource('point', {
          type: 'geojson',
          data: geojson,
        });

        this.map.addLayer({
          id: 'point',
          type: 'circle',
          source: 'point',
          paint: {
            'circle-radius': 20,
            'circle-color': '#2979FF',
          },
        });

        this.map.on('mouseenter', 'point', () => {
          this.map.setPaintProperty('point', 'circle-color', '#3bb2d0');
          canvas.style.cursor = 'move';
        });

        this.map.on('mouseleave', 'point', () => {
          this.map.setPaintProperty('point', 'circle-color', '#2979FF');
          canvas.style.cursor = '';
        });

        this.map.on('mousedown', 'point', (e) => {
          e.preventDefault();

          canvas.style.cursor = 'grab';

          this.map.on('mousemove', onMove);
          this.map.once('mouseup', onUp);
        });

        this.map.on('touchstart', 'point', (e) => {
          if (e.points.length !== 1) return;

          e.preventDefault();

          this.map.on('touchmove', onMove);
          this.map.once('touchend', onUp);
        });
      } else {
        this.map.removeLayer('point');
        this.map.removeSource('point');
      }
    },
  },
};
</script>
